import { Box, Column, Row, Text, Tooltip } from "@hightouch/htui";

import { countOf, PROJECTS_TERMS as TERMS } from "src/pages/projects/constants";
import {
  CATEGORY_COLORS,
  projectProgress,
  STATUS_CATEGORY_LABELS,
  type StatusCategory,
} from "src/pages/projects/utils";

// Finished work fills from the left, then work under way; the backlog is the empty track.
const SEGMENT_ORDER: StatusCategory[] = ["done", "review", "active"];

type Props = {
  project: Parameters<typeof projectProgress>[0];
  /** "sm" for rows and headers, "md" for a project's own page. */
  size?: "sm" | "md";
};

/** A project's tasks by status category, as one bar in the categories' colors. */
export const ProjectProgressBar = ({ project, size = "sm" }: Props) => {
  const { total, completed, byCategory } = projectProgress(project);
  const label = `${completed} of ${countOf(total, TERMS.task)} complete`;
  const counted = [...SEGMENT_ORDER, "backlog" as const].filter(
    (category) => byCategory[category] > 0,
  );

  return (
    <Tooltip
      isDisabled={total === 0}
      message={
        <Column gap={1}>
          <Text size="sm" fontWeight="medium" color="inherit">
            {label}
          </Text>
          {counted.map((category) => (
            <Row key={category} align="center" gap={2}>
              <Box
                boxSize={2}
                borderRadius="full"
                bg={CATEGORY_COLORS[category]}
              />
              <Text size="sm" color="inherit">
                {`${STATUS_CATEGORY_LABELS[category]}: ${byCategory[category]}`}
              </Text>
            </Row>
          ))}
        </Column>
      }
    >
      <Row
        role="progressbar"
        aria-label={label}
        aria-valuemin={0}
        aria-valuemax={total}
        aria-valuenow={completed}
        width="100%"
        height={size === "md" ? 2 : 1.5}
        borderRadius="full"
        overflow="hidden"
        bg="base.divider"
        gap="1px"
      >
        {total > 0 &&
          SEGMENT_ORDER.map((category) =>
            byCategory[category] > 0 ? (
              <Box
                key={category}
                height="100%"
                bg={CATEGORY_COLORS[category]}
                flexBasis={`${(byCategory[category] / total) * 100}%`}
                flexShrink={0}
              />
            ) : null,
          )}
      </Row>
    </Tooltip>
  );
};
